import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import { Controller, useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'

import { useCreateKeywordMutation, useLazyListKeywordsQuery } from '../../../services/options'
import {
  getOption,
  useAbstractSourceOptions,
  useAbstractTypeOptions,
  useDomainsDataOptions,
  useThemesDataOptions,
  useKeywordsDataOptions,
  searchString,
  Options,
} from '../../../utils'
import { AbstractSchema } from '../Abstracts.schema'
import AuthorsAutocomplete from './AuthorsAutocomplete'
import Dropzone from '../../root/components/Dropzone'
import { AbstractSource } from '../../../types'
import { useDeleteAbstractFigureMutation } from '../../../services/events'

interface KeywordsOptions extends Options {
  inputValue?: string
}
const filter = createFilterOptions<KeywordsOptions | null>()

export default function AbstractForm({ readOnly = false }: { readOnly?: boolean }) {
  const { eventId, abstractId } = useParams()
  const { t } = useTranslation('abstracts')

  const typesOptions = useAbstractTypeOptions(t)
  const sourcesOptions = useAbstractSourceOptions(t)
  const domainsOptions = useDomainsDataOptions()
  const themesOptions = useThemesDataOptions()

  const [listKeywords, { isLoading: isQueryLoading, data: keywordsData }] =
    useLazyListKeywordsQuery()
  const [createKeyword, { isLoading: isMutationLoading }] = useCreateKeywordMutation()
  const isLoading = isQueryLoading || isMutationLoading

  const [deleteAbstractFigure] = useDeleteAbstractFigureMutation()

  const keywordsOptions = useKeywordsDataOptions(keywordsData?.results)

  const { control, watch, getValues, setValue } = useFormContext<AbstractSchema>()

  const source = watch('source')

  const onSubmitNewKeyword = (keyword: string) => {
    if (window.confirm(t('Êtes-vous sûr de vouloir ajouter ce mot-clé ?') + ` "${keyword}"`)) {
      createKeyword(keyword)
        .then(({ data }) => {
          if (data?.id) {
            toast.success(t('Mot-clé ajouté avec succès'))
            listKeywords()
              .then(() => {
                const values = getValues('keywords') ?? []
                setValue('keywords', [...values, data.id])
              })
              .catch(err => {
                toast.error(t('Une erreur est survenue lors de la récupération des mots-clés'))
                console.error(err)
              })
          }
        })
        .catch(err => {
          toast.error(t('Une erreur est survenue lors de la création du mot-clé'))
          console.error(err)
        })
    }
  }

  return (
    <Stack gap="1rem">
      <Typography variant="h3">{t('Informations')}</Typography>
      <Divider sx={{ margin: 0 }} />

      <Controller
        control={control}
        name="type"
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => field.onChange(value?.value)}
            onBlur={field.onBlur}
            defaultValue={getOption(typesOptions, null, field.value)}
            options={typesOptions}
            id="type-autocomplete"
            disablePortal
            fullWidth
            readOnly={readOnly}
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={field.disabled}
                label={t('Type de résumé')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="authors"
        render={({ field, fieldState }) => (
          <AuthorsAutocomplete
            prependName={field.name}
            defaultValue={field.value}
            onChange={field.onChange}
            label={t('Auteurs')}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            readOnly={readOnly}
          />
        )}
      />
      <Controller
        control={control}
        name="source"
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => field.onChange(value?.value ?? '')}
            onBlur={field.onBlur}
            defaultValue={getOption(sourcesOptions, null, field.value)}
            options={sourcesOptions}
            id="source-autocomplete"
            readOnly={readOnly}
            disablePortal
            fullWidth
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={field.disabled}
                label={t('Source')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="source_other"
        rules={{ required: source === AbstractSource.Other }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Autre source')}
            fullWidth
            required={source === AbstractSource.Other}
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            sx={{ display: source === AbstractSource.Other ? undefined : 'none' }}
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="domain"
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => field.onChange(value?.value ?? '')}
            onBlur={field.onBlur}
            defaultValue={getOption(domainsOptions, null, field.value)}
            options={domainsOptions}
            id="domain-autocomplete"
            disablePortal
            readOnly={readOnly}
            fullWidth
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={field.disabled}
                label={t('Domaine')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="theme"
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <Autocomplete
            onChange={(_, value) => field.onChange(value?.value ?? '')}
            onBlur={field.onBlur}
            defaultValue={getOption(themesOptions, null, field.value)}
            options={themesOptions}
            readOnly={readOnly}
            // Filter options when the user types. It should look for the theme and the sub-theme (label and group field)
            filterOptions={(options, state) =>
              options.filter(
                option =>
                  searchString(option.label, state.inputValue) ||
                  searchString(option.group, state.inputValue)
              )
            }
            id="theme-autocomplete"
            groupBy={option => option.group}
            disablePortal
            fullWidth
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={field.disabled}
                label={t('Thème et sous-thème')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="keywords"
        render={({ field, fieldState }) => (
          <Autocomplete
            id="keywords-autocomplete"
            onChange={(_, value) => {
              const res = value.map(val => {
                if (typeof val === 'string') {
                  // Create a new value from the user input
                  onSubmitNewKeyword(val)
                  return undefined
                } else if (val && val.inputValue) {
                  // Create a new value from the user input
                  onSubmitNewKeyword(val.inputValue)
                  return undefined
                } else {
                  return val?.value
                }
              })
              field.onChange(res.filter(Boolean))
            }}
            onBlur={field.onBlur}
            value={field.value?.map(val => getOption(keywordsOptions, null, val)).filter(Boolean)}
            options={keywordsOptions}
            readOnly={readOnly}
            multiple
            disablePortal
            selectOnFocus
            clearOnBlur
            freeSolo
            fullWidth
            loading={isLoading}
            disabled={isLoading}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)

              const { inputValue } = params
              // Suggest the creation of a new value
              const isExisting = options.some(option => inputValue === option?.label)
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  value: -1,
                  inputValue,
                  label: `${t('Ajouter')} "${inputValue}"`,
                })
              }

              return filtered
            }}
            getOptionLabel={option => {
              // Value selected with enter, right from the input
              if (typeof option === 'string') {
                return option
              }
              // Regular option
              if (option?.label) {
                return option.label
              }
              // Null
              return ''
            }}
            renderInput={params => (
              <TextField
                {...params}
                name={field.name}
                disabled={isLoading || field.disabled}
                label={t('Mots-clés')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}
      />
      <Typography variant="h3">{t('Résumé')}</Typography>
      <Divider sx={{ margin: 0 }} />
      <Controller
        control={control}
        name="title"
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Titre')}
            fullWidth
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            required
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="title_en"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Titre en anglais')}
            fullWidth
            variant="filled"
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="objective"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Objectif')}
            fullWidth
            multiline
            minRows={2}
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            required
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="objective_en"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Objectif en anglais')}
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="materials_and_methods"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Matériaux et méthodes')}
            fullWidth
            multiline
            minRows={2}
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            required
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="materials_and_methods_en"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Matériaux et méthodes en anglais')}
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="results"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Résultats')}
            fullWidth
            multiline
            minRows={2}
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            required
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="results_en"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Résultats en anglais')}
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="conclusions"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Conclusion')}
            fullWidth
            multiline
            minRows={2}
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            required
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="conclusions_en"
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={t('Conclusion en anglais')}
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            slotProps={{
              input: { readOnly: readOnly },
            }}
          />
        )}
      />
      <Typography variant="h3">{t('Figures')}</Typography>
      <Divider sx={{ margin: 0 }} />
      <Controller
        control={control}
        name="figures"
        render={({ field, fieldState }) => (
          <Dropzone
            accept={{
              'image/*': [],
            }}
            onChange={files => {
              field.onChange(files)
            }}
            onDelete={file => {
              const promise = new Promise<typeof file>((resolve, reject) => {
                if (window.confirm(t('Êtes-vous sûr de vouloir supprimer cette figure ?'))) {
                  deleteAbstractFigure({
                    eventId: Number(eventId),
                    abstractId: Number(abstractId),
                    id: file.id,
                  })
                    .then(data => {
                      if ('data' in data) {
                        toast.success(t('Figure supprimée avec succès'))
                        field.onChange(field.value.filter(f => f.title !== file.title))
                        resolve(file)
                      } else {
                        reject(file)
                      }
                    })
                    .catch(err => {
                      toast.error(t('Une erreur est survenue lors de la suppression de la figure'))
                      console.error(err)
                      reject(file)
                    })
                } else {
                  reject(file)
                }
              })

              return promise
            }}
            error={fieldState.error?.message !== undefined}
            helperText={fieldState.error?.message}
            defaultValue={field.value}
            disabled={readOnly}
          />
        )}
      />
    </Stack>
  )
}
